import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography, Avatar, CircularProgress } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import dayjs from 'dayjs';
import { getTaksActivityLogs, clearTaskActivityLogs } from '../../../redux/features/taskManagementSlice';

const AV_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#3B82F6', '#F43F5E'];
const avatarColor = (name = '') =>
  AV_COLORS[Math.abs((name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AV_COLORS.length];

const fmt = (t) => (t && dayjs(t).isValid() ? dayjs(t).format('DD MMM YYYY, hh:mm A') : '');

const normalizeLog = (a = {}, idx = 0) => {
  const nv = a.new_value && typeof a.new_value === 'object' ? a.new_value : {};
  return {
    id: a.history_log_id ?? a.activity_id ?? a.id ?? a.log_id ?? idx,
    text:
      a.action_label ??
      a.activity ??
      a.action ??
      a.description ??
      a.message ??
      a.log_message ??
      a.activity_type ??
      'Activity',
    // secondary detail pulled from the change payload when available
    detail: nv.snippet ?? nv.title ?? '',
    actor:
      a.created_by_name ??
      a.performed_by_name ??
      a.user_name ??
      a.done_by_name ??
      a.name ??
      'System',
    time: a.created_at ?? a.activity_time ?? a.createdAt ?? a.timestamp ?? a.updated_at,
  };
};

const TaskActivity = ({ taskId }) => {
  const dispatch = useDispatch();
  const { taskActivityLogs } = useSelector((state) => state.taskManagement);
  const [fetching, setFetching] = useState(false);
  // taskId whose data is currently in the store — until this matches the active
  // taskId we show a spinner instead of the previous task's (stale) logs.
  const [loadedFor, setLoadedFor] = useState(null);

  const logs = useMemo(
    () => (Array.isArray(taskActivityLogs) ? taskActivityLogs.map(normalizeLog) : []),
    [taskActivityLogs]
  );

  useEffect(() => {
    if (!taskId) return;
    dispatch(clearTaskActivityLogs()); // drop previous task's logs immediately
    setFetching(true);
    dispatch(getTaksActivityLogs(taskId)).finally(() => {
      setFetching(false);
      setLoadedFor(taskId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const ready = loadedFor === taskId;

  if (!ready) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress size={26} />
      </Stack>
    );
  }

  if (logs.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, color: '#94A3B8' }}>
        <HistoryIcon sx={{ fontSize: 42, color: '#CBD5E1' }} />
        <Typography sx={{ fontSize: '0.85rem' }}>No activity recorded for this task yet.</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
      <Stack spacing={0}>
        {logs.map((log, i) => {
          const last = i === logs.length - 1;
          return (
            <Stack key={log.id} direction="row" spacing={1.5} sx={{ position: 'relative' }}>
              {/* Timeline rail */}
              <Stack alignItems="center" sx={{ width: 34, flexShrink: 0 }}>
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    bgcolor: `${avatarColor(log.actor)}22`,
                    color: avatarColor(log.actor),
                  }}
                >
                  {log.actor?.charAt(0)?.toUpperCase() || '•'}
                </Avatar>
                {!last && <Box sx={{ flex: 1, width: 2, background: '#E2E8F0', my: 0.5, minHeight: 18 }} />}
              </Stack>

              {/* Content */}
              <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2 }}>
                <Box
                  sx={{
                    background: '#F8FAFC',
                    border: '1px solid #EEF2F6',
                    borderRadius: 2,
                    px: 1.75,
                    py: 1.1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#334155',
                      lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {log.text}
                  </Typography>
                  {log.detail && (
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        color: '#64748B',
                        lineHeight: 1.5,
                        mt: 0.25,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {log.detail}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6366F1' }} noWrap>
                      {log.actor}
                    </Typography>
                    {log.time && (
                      <>
                        <Box sx={{ width: 3, height: 3, borderRadius: '50%', background: '#CBD5E1' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }} noWrap>
                          {fmt(log.time)}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default TaskActivity;
